import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Home, BookOpen, Trophy, User, Settings, Library, Target } from "lucide-react";
import { SCOPE_HOME, isStudioUser } from "./ScopedAccessGuard";

export const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Studio lock: the Reclaim nav only exists for admins and studio testers.
  if (!isStudioUser(user)) return null;
  const tester = user?.access_scope && user.access_scope !== "football_preview" && user.access_scope !== "studio_all";

  // Scoped-access users get a bespoke, minimal nav — hides everything
  // outside their allow-list so they never see routes they can't visit.
  if (user?.access_scope === "football_preview" || tester) {
    const scopedTabs = tester ? [
      { path: SCOPE_HOME[user.access_scope], icon: Home, label: "My area" },
      { path: "/account", icon: User, label: "Account" },
    ] : [
      { path: "/football", icon: Home, label: "Football" },
      { path: "/golf", icon: Target, label: "Golf" },
      { path: "/practices/state-tuning", icon: BookOpen, label: "Resets" },
      { path: "/daily-essentials", icon: Trophy, label: "Rituals" },
      { path: "/account", icon: User, label: "Account" },
    ];
    return (
      <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-[#E8E6E1] z-50 shadow-[0_-4px_16px_rgba(26,29,26,0.06)]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="max-w-lg mx-auto flex justify-around items-center h-14">
          {scopedTabs.map(({ path, icon: Icon, label }) => {
            const isActive = location.pathname === path ||
              (path !== "/" && location.pathname.startsWith(path));
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                data-testid={`nav-${label.toLowerCase()}`}
                className={`relative flex flex-col items-center justify-center gap-0.5 px-3 py-1.5 min-w-[56px] transition-all duration-200 ${
                  isActive ? "text-[#4A6741]" : "text-[#8B8680]"
                }`}
              >
                <Icon size={20} strokeWidth={isActive ? 2.2 : 1.8} />
                <span className="text-[10px] font-medium">{label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    );
  }

  const tabs = [
    { path: "/reclaim-home", icon: Home, label: "Home" },
    { path: "/programs", icon: BookOpen, label: "Programs" },
    // 'Library' replaces the old 'Studio' tab (Feb 12 2026). Voice
    // Studio recording still lives at /voice-studio (linked from Library
    // + Account), but bottom-nav now points at the unified My Collection
    // — saved moments, immersions, audios, all in one place.
    { path: "/library", icon: Library, label: "Library" },
    { path: "/challenges", icon: Trophy, label: "Challenges" },
    { path: "/account", icon: User, label: "Account" },
  ];

  if (user?.role === "admin") {
    tabs.push({ path: "/admin", icon: Settings, label: "Admin" });
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-[#E8E6E1] z-50 shadow-[0_-4px_16px_rgba(26,29,26,0.06)]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="max-w-lg mx-auto flex justify-around items-center h-14">
        {tabs.map(({ path, icon: Icon, label }) => {
          const isActive = location.pathname === path || 
            (path !== "/" && location.pathname.startsWith(path));
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              data-testid={`nav-${label.toLowerCase()}`}
              className={`
                relative flex flex-col items-center justify-center gap-0.5 px-3 py-1.5 min-w-[56px]
                transition-all duration-200
                ${isActive 
                  ? "text-[#2D4A2A]" 
                  : "text-[#9A9F9A] active:text-[#4A4F4A]"
                }
              `}
            >
              <Icon 
                size={20} 
                strokeWidth={isActive ? 2.5 : 1.8}
              />
              <span className={`
                text-[9px] font-medium
                ${isActive ? "opacity-100" : "opacity-70"}
              `}>
                {label}
              </span>
              
              {/* Active indicator dot */}
              {isActive && (
                <span className="absolute -bottom-0.5 w-1 h-1 bg-[#2D4A2A] rounded-full" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};
